import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { object, string, boolean, number } from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  useTheme,
  InputAdornment,
} from "@mui/material";
import {
  Tag,
  DollarSign,
  Plus,
  Image as ImageIcon,
  Hash
} from 'lucide-react';
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { createAddOn } from "./AddOnsService";
import { getAllAddOnCategories } from "./AddOnCategoriesService";
import { AddOnShape } from "../../utils/models/AddOnModel";
import { EntitiesListResponseModel } from "../../utils/models/EntitiesListResponseModel";
import FileChip from "../../utils/components/FileChip";
import { tokens } from "../../theme";

const addOnSchema = object().shape({
  id: number().notRequired(),
  name: string().required("Name is required"),
  categoryId: number().required("Category is required"),
  description: string().required("Description is required"),
  price: number()
    .required("Price is required")
    .positive("Price must be greater than zero"),
  active: boolean().required("Active is required"),
});

const AddOnForm = (props) => {
  const {
    addOn,
    updateAddOn,
    toggleModal,
    setState,
    isUpdating = false,
  } = props;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const setIsLoading = useLoadingSpinner();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState(addOn?.media ? [addOn.media] : []);
  const [categoriesData, setCategoriesData] = useState(EntitiesListResponseModel);
  const setApiError = () => {};

  const availableCategories = categoriesData.content.filter(
    (category) => category.active || category.id === addOn?.categoryId,
  );

  const {
    clearErrors,
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addOnSchema),
    defaultValues: {
      id: addOn?.id ?? null,
      name: addOn?.name ?? "",
      categoryId: addOn?.categoryId ?? null,
      description: addOn?.description ?? "",
      price: addOn?.price ?? "",
      active: addOn?.active ?? addOn?.isActive ?? true,
    },
  });

  useEffect(() => {
    reset({
      id: addOn?.id ?? null,
      name: addOn?.name ?? "",
      categoryId: addOn?.categoryId ?? null,
      description: addOn?.description ?? "",
      price: addOn?.price ?? "",
      active: addOn?.active ?? addOn?.isActive ?? true,
    });
    setFiles(addOn?.media ? [addOn.media] : []);
  }, [addOn, reset]);

  useEffect(() => {
    getAllAddOnCategories(
      setCategoriesData,
      { pageSize: "All", page: 0 },
      undefined,
      undefined,
    ).catch(() => {
      // Toast handled in service.
    });
  }, []);

  const handleCancel = () => {
    clearErrors();
    reset();
    toggleModal(false);
  };

  const onSubmit = async (data) => {
    const selectedCategory = categoriesData.content.find(
      (category) => category.id === Number(data.categoryId),
    );

    const updatedAddOn = {
      ...data,
      categoryId: Number(data.categoryId),
      category: selectedCategory?.name ?? addOn?.category ?? "",
      active: Boolean(data.active),
      isActive: Boolean(data.active),
      media: addOn?.media ?? null,
      mediaId: addOn?.media?.id ?? addOn?.mediaId ?? null,
      addonType: addOn?.addonType ?? addOn?.type ?? null,
    };
    let image = null;
    if (files.length > 0 && files[0] !== addOn?.media) {
      image = files[0];
    }

    setIsLoading(true);

    try {
      if (isUpdating) {
        await updateAddOn(
          updatedAddOn,
          image,
          setState,
          setApiError,
          setIsLoading
        );
      } else {
        await createAddOn(
          updatedAddOn,
          image,
          setState,
          setApiError,
          setIsLoading
        );
      }

      handleCancel();
    } catch (err) {
      console.error("Submission failed", err);
    }
  };

  const fileChangeHandler = (event) => {
    const file = event.target.files;
    if (file.length === 0) {
      setFiles([]);
    } else {
      setFiles(Array.from(file));
    }
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 3, md: 5 },
        borderRadius: "24px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h3" fontWeight="700" sx={{ mb: 1, color: colors.grey[100], fontFamily: "Josefin Sans" }}>
              {isUpdating ? "Update Add-On" : "Add New Experience"}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.grey[300], mb: 2 }}>
              Define the details and pricing for this premium service offering.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Service Name"
              {...register("name")}
              error={!!errors.name}
              helperText={errors.name?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Hash size={18} color={colors.grey[400]} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.categoryId}>
              <InputLabel id="category-label">Category</InputLabel>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="category-label"
                    label="Category"
                    value={field.value ?? ""}
                    startAdornment={<Tag size={18} style={{ marginRight: "8px", color: colors.grey[400] }} />}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  >
                    <MenuItem value="">Select category</MenuItem>
                    {availableCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        <ListItemText
                          primary={category.name}
                          secondary={category.description || "No description provided"}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors.categoryId?.message}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Base Price"
              type="number"
              {...register("price")}
              error={!!errors.price}
              helperText={errors.price?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DollarSign size={18} color={colors.grey[400]} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', ml: 1 }}>
              <Controller
                name="active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="secondary"
                      />
                    }
                    label={
                      <Typography fontWeight="600" color={colors.grey[100]}>
                        Mark as Active
                      </Typography>
                    }
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                borderRadius: "16px",
                border: `2px dashed ${theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]}`,
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: colors.grey[100] }}>
                Feature Image
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                {files.length > 0 ? (
                  files.map((file, index) => (
                    <FileChip
                      key={file?.id ?? file?.name ?? `file-${file?.lastModified ?? index}`}
                      file={file}
                      onDelete={() => setFiles([])}
                    />
                  ))
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                    <ImageIcon size={48} color={colors.grey[400]} style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <Typography color={colors.grey[400]}>No image selected</Typography>
                  </Box>
                )}
              </Box>

              <Button
                variant="outlined"
                startIcon={<Plus size={18} />}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: "600",
                  borderColor: colors.grey[300],
                  color: colors.grey[100],
                  "&:hover": {
                    borderColor: colors.greenAccent[500],
                    bgcolor: `${colors.greenAccent[500]}0D`
                  }
                }}
              >
                Upload Image
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/*"
                onChange={fileChangeHandler}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Service Description"
              {...register("description")}
              error={!!errors.description}
              helperText={errors.description?.message}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
                "&:hover": { borderColor: colors.redAccent[600], bgcolor: `${colors.redAccent[500]}0D` }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                bgcolor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
              }}
            >
              {isUpdating ? 'Update Service' : 'Create Service'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

AddOnForm.propTypes = {
  addOn: PropTypes.shape(AddOnShape),
  updateAddOn: PropTypes.func,
  toggleModal: PropTypes.func.isRequired,
  setState: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool,
};

export default AddOnForm;
